import React, { useEffect, useState } from "react";
import Slider from "react-slick";
import {
  Box,
  Card,
  CardMedia,
  Typography,
  Container,
  Skeleton,
} from "@mui/material";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./ProductDealCarousel.css";
import { AxiosResponse } from "axios";
import { serviceFetchProductDeals } from "src/api/service/services";
import defaultBanner from "../../assets/default_sale_tag.png"; // ✅ your default fallback image
import { getRetailerLogo } from "src/utils/retailers_list";

const ProductDealsCarousel = () => {
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [productDealData, setProductDealData] = useState<any[]>([]);

  useEffect(() => {
    serviceFetchProductDeals().then((response: AxiosResponse) => {
      const data = response?.data || [];

      // ✅ Exclude deals with no valid URL
      const validDeals = data.filter(
        (deal: any) => deal?.acf?.product_deals_url?.trim()
      );

      setProductDealData(validDeals);
    });
  }, []);

  const handleImagesLoad = () => {
    setImagesLoaded(true);
  };

  const settings = {
    infinite: true,
    speed: 500,
    slidesToShow: 2,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 2500,
    arrows: false,
    responsive: [
      { breakpoint: 1200, settings: { slidesToShow: 2 } },
      { breakpoint: 768, settings: { slidesToShow: 1 } },
    ],
  };

  // ✅ Don’t show skeleton if API returned empty data
  if (!productDealData.length) return null;

  return (
    <Box
      sx={{
        background:
          "linear-gradient(90.01deg, #790009 0.01%, #420858 69.46%, #1D0E8D 107.77%)",
        borderRadius: "12px",
        p: "10px",
      }}
    >
      <Container maxWidth={false} sx={{ px: { xs: 1, md: 2 } }}>
        <Typography
          variant="h5"
          align="center"
          sx={{
            color: "#fff",
            fontWeight: 700,
            textShadow: "1px 1px 5px rgba(0,0,0,0.4)",
            m:"24px"
          }}
        >
          💥 Hot Product Deals
        </Typography>

        {!imagesLoaded && (
          <Skeleton variant="rounded" height={100} animation="wave" />
        )}

        <Slider {...settings} className="product-deals-slider">
          {productDealData.map((deal: any, index: number) => {
            const retailer = deal?.acf?.product_deals_retailer || "";
            const retailerLogo = getRetailerLogo(retailer);
            const imageUrl =
              deal?.acf?.product_deals_image?.trim() || defaultBanner;

            return (
              <Box key={deal?.id || index}>
                <Card
                  sx={{
                    margin: "8px",
                    borderRadius: "16px",
                    overflow: "hidden",
                    boxShadow: "0px 4px 20px rgba(0,0,0,0.25)",
                    transition: "transform 0.3s ease",
                    "&:hover": { transform: "scale(1.05)" },
                    position: "relative",
                  }}
                >
                  {/* Product Deal Image */}
                  <CardMedia
                    component="img"
                    src={imageUrl}
                    alt={deal?.acf?.product_deals_alt || `deal-${index + 1}`}
                    onClick={() =>
                      deal?.acf?.product_deals_url &&
                      window.open(deal?.acf?.product_deals_url, "_blank")
                    }
                    onLoad={handleImagesLoad}
                    sx={{
                      width: "100%",
                      height: 250,
                      objectFit: "contain",
                      backgroundColor: "#fff",
                      cursor: deal?.acf?.product_deals_url
                        ? "pointer"
                        : "default",
                    }}
                  />

                  {/* 🛒 Retailer Logo Overlay */}
                  {retailer && (
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 10,
                        right: 10,
                        backgroundColor: "rgba(255,255,255,0.9)",
                        borderRadius: "8px",
                        padding: "4px 8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                      }}
                    >
                      <img
                        src={retailerLogo}
                        alt={retailer}
                        style={{
                          width: 60,
                          height: "auto",
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                  )}
                </Card>
              </Box>
            );
          })}
        </Slider>
      </Container>
    </Box>
  );
};

export default ProductDealsCarousel;
